import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import Backdrop from '@material-ui/core/Backdrop';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import RestoreIcon from '@material-ui/icons/Restore';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { useSnackbar } from 'notistack';

import { getListAllAPI, api } from '../../services';
import DialogoEditarEndereco from './DialogoCreateEdit';
import DialogoShow from './DialogoShow';
import {
  CATEGORIAS_BLOCO_MAPA_LOJA,
  CATEGORIAS_ENDERECO_EXPOSICAO,
  STATUS_ATIVO,
} from '../../utils/constants';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: 'black',
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: 10000,
    color: '#fff',
  },
  content: {
    display: 'flex',
    height: '100%',
    padding: 0,
    paddingBottom: theme.spacing(5),
    overflow: 'hidden',
  },
  sidebar: {
    width: '260px',
    flexShrink: 0,
    borderRight: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  sidebarDireita: {
    width: '260px',
    flexShrink: 0,
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  cabecalhoFixo: {
    flexShrink: 0,
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  listaScrollavel: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  cabecalhoSidebar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: 4,
  },
  dicaSidebar: {
    display: 'block',
    marginBottom: theme.spacing(1.5),
  },
  divisorSidebar: {
    marginBottom: 0,
  },
  categoriaChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '8px',
    border: '2px solid transparent',
    padding: '6px 8px',
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    userSelect: 'none',
  },
  categoriaChipSelecionada: {
    borderColor: '#000',
  },
  categoriaCor: {
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  // O wrapper não rola (overflow: hidden) e serve só de âncora pros controles
  // de zoom flutuantes, que assim ficam sempre visíveis no canto — se o zoom
  // estivesse ligado ao elemento que rola, os botões sumiriam de vista ao
  // arrastar o mapa.
  areaMapaWrapper: {
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
  },
  areaMapa: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    // "safe center" evita o problema clássico de flexbox onde um conteúdo
    // centralizado maior que o container fica com uma parte inacessível
    // mesmo rolando — sem isso, dar zoom além do tamanho da tela cortaria
    // pedaços do grid que a rolagem não alcançaria.
    alignItems: 'safe center',
    justifyContent: 'safe center',
  },
  controlesZoom: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    padding: '2px',
    zIndex: 1,
  },
  legendaCategoriaEndereco: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: theme.spacing(1),
  },
  legendaCategoriaEnderecoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendaCategoriaEnderecoContagem: {
    marginLeft: '20px',
  },
  legendaCategoriaEnderecoCor: {
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    flexShrink: 0,
  },
  labelZoom: {
    minWidth: '40px',
    textAlign: 'center',
  },
  cardEndereco: {
    width: '64px',
    height: '64px',
    borderRadius: '8px',
    borderStyle: 'solid',
    borderWidth: '1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '4px',
    boxSizing: 'border-box',
    cursor: 'grab',
    userSelect: 'none',
    transition: 'box-shadow 0.15s ease',
    '&:hover': {
      boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
    },
  },
  listaCardsEnderecos: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
  },
  grid: {
    display: 'grid',
  },
  celula: {
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    userSelect: 'none',
  },
  celulaOcupada: {
    borderStyle: 'solid',
    cursor: 'grab',
  },
  celulaConteudo: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    overflow: 'hidden',
    padding: '2px',
    boxSizing: 'border-box',
  },
}));

function getChaveCelula(x, y) {
  return `${x}-${y}`;
}

// Desativado por enquanto a pedido — os blocos por categoria (paredes, área
// neutra, depósito) ficam escondidos da interface, mas o código continua
// intacto pra reativar rápido no futuro só virando isso pra "true" de novo.
const BLOCOS_CATEGORIA_HABILITADO = false;

const DialogoMapaLoja = forwardRef((props, ref) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [enderecos, setEnderecos] = useState([]);
  const [blocos, setBlocos] = useState([]);
  const [largura, setLargura] = useState(10);
  const [altura, setAltura] = useState(10);
  const [mapaLojaId, setMapaLojaId] = useState(null);
  const [nomeMapa, setNomeMapa] = useState('');
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [dialogoEditarEndereco, setDialogoEditarEndereco] = useState({
    open: false,
    id: null,
  });
  const [menuEndereco, setMenuEndereco] = useState({ endereco: null });
  const [idEnderecoVisualizado, setIdEnderecoVisualizado] = useState(null);
  const refDialogoShow = useRef(null);

  const mouseDownRef = useRef(false);
  const modoArrasteRef = useRef('pintar');
  const areaMapaRef = useRef(null);
  const [tamanhoBase, setTamanhoBase] = useState(20);
  const [zoom, setZoom] = useState(1.75);

  const GAP_CELULA = 2;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 5;
  const ZOOM_PASSO = 0.25;

  const tamanhoCelula = tamanhoBase * zoom;

  function handleZoomIn() {
    setZoom((prev) => Math.min(ZOOM_MAX, Math.round((prev + ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomOut() {
    setZoom((prev) => Math.max(ZOOM_MIN, Math.round((prev - ZOOM_PASSO) * 100) / 100));
  }

  function handleZoomReset() {
    setZoom(1);
  }

  useEffect(() => {
    const elemento = areaMapaRef.current;
    if (!elemento) return undefined;

    function recalcular() {
      const { clientWidth, clientHeight } = elemento;
      const larguraDisponivel = clientWidth - GAP_CELULA * (largura - 1);
      const alturaDisponivel = clientHeight - GAP_CELULA * (altura - 1);
      const tamanho = Math.floor(
        Math.min(larguraDisponivel / largura, alturaDisponivel / altura),
      );
      setTamanhoBase(Math.max(tamanho, 4));
    }

    recalcular();

    const observer = new ResizeObserver(recalcular);
    observer.observe(elemento);
    return () => observer.disconnect();
  }, [largura, altura, carregando]);

  useEffect(() => {
    function handleMouseUp() {
      mouseDownRef.current = false;
    }
    // "dragend" cobre o caso de um drag nativo do navegador (ex: arrastar o
    // texto selecionado de um quadrado) começar no meio da pintura — nesse
    // caso o "mouseup" não dispara enquanto o drag estiver ativo, e o ref
    // ficaria preso em "true", pintando tudo que o mouse passasse por cima
    // depois, mesmo sem o botão pressionado.
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('dragend', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('dragend', handleMouseUp);
    };
  }, []);

  async function getDataResource(idMapa) {
    setCarregando(true);
    try {
      const dataMapa = await getListAllAPI(
        'mapas_loja',
        ['id', 'asc'],
        { id: [idMapa] },
        [],
      );
      if (dataMapa.data.length === 0) throw 'Este mapa não existe!';

      const dataEnderecos = await getListAllAPI(
        'enderecos_exposicao_mercadorias',
        ['codigo', 'asc'],
        { status: STATUS_ATIVO, mapa_loja_id: idMapa },
        ['produto', 'andaresProdutos'],
      );

      let dataBlocos = { data: [] };
      if (BLOCOS_CATEGORIA_HABILITADO) {
        dataBlocos = await getListAllAPI(
          'blocos_mapa_loja',
          ['id', 'asc'],
          { mapa_loja_id: idMapa },
          [],
        );
      }

      setLargura(dataMapa.data[0].largura);
      setAltura(dataMapa.data[0].altura);
      setNomeMapa(dataMapa.data[0].nome);
      setEnderecos(dataEnderecos.data);
      setBlocos(dataBlocos.data);
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }

  useImperativeHandle(ref, () => ({
    handleOpen(idMapa) {
      setOpen(true);
      setMapaLojaId(idMapa);
      setCategoriaSelecionada(null);
      setZoom(1.75);
      getDataResource(idMapa);
    },
  }));

  function handleClose() {
    setOpen(false);
  }

  async function posicionar(id, x, y) {
    // Ao remover a posição (arrastar de volta pra "sem posição"), o endereço
    // precisa voltar a ficar livre (sem mapa) — senão ele fica preso a este
    // mapa e some da lista de disponíveis dos outros.
    const removendo = x === null && y === null;
    const novoMapaLojaId = removendo ? null : mapaLojaId;

    setEnderecos((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, posicao_x: x, posicao_y: y, mapa_loja_id: novoMapaLojaId }
          : e,
      ),
    );

    const response = await api.put(`/enderecos_exposicao_mercadorias/posicao/${id}`, {
      posicao_x: x,
      posicao_y: y,
      mapa_loja_id: novoMapaLojaId,
    });

    if (!response.data.status) {
      enqueueSnackbar(response.data.message, { variant: 'error' });
    }
  }

  // Usa um tipo MIME próprio (em vez de "text/plain") pra diferenciar um
  // drag nosso (arrastar o card do endereço) de um drag nativo do navegador
  // (ex: selecionar e arrastar o texto de dentro do quadrado sem querer) —
  // esse segundo caso nunca preenche esse tipo, então o drop é ignorado.
  function handleDragStart(event, id) {
    event.dataTransfer.setData('application/x-endereco-id', String(id));
  }

  function handleDragOver(event) {
    event.preventDefault();
  }

  function handleDropRemover(event) {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/x-endereco-id');
    if (!raw) return;

    const id = parseInt(raw, 10);
    if (Number.isNaN(id)) return;

    posicionar(id, null, null);
  }

  function handleDrop(event, x, y) {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/x-endereco-id');
    if (!raw) return;

    const id = parseInt(raw, 10);
    if (Number.isNaN(id)) return;

    const ocupanteEndereco = enderecos.find(
      (e) => e.posicao_x === x && e.posicao_y === y,
    );
    if (ocupanteEndereco && ocupanteEndereco.id !== id) {
      enqueueSnackbar('Esta posição já está ocupada por outro endereço!', {
        variant: 'error',
      });
      return;
    }

    const ocupanteBloco = blocos.find((b) => b.posicao_x === x && b.posicao_y === y);
    if (ocupanteBloco) {
      enqueueSnackbar('Esta posição já está ocupada por um bloco!', {
        variant: 'error',
      });
      return;
    }

    posicionar(id, x, y);
  }

  async function criarBloco(x, y) {
    const categoria = categoriaSelecionada;

    const resposta = await api.post('/blocos_mapa_loja', {
      categoria,
      posicao_x: x,
      posicao_y: y,
      mapa_loja_id: mapaLojaId,
    });

    if (resposta.data.status) {
      setBlocos((prev) => [...prev, resposta.data.data]);
    } else {
      enqueueSnackbar(resposta.data.message, { variant: 'error' });
    }
  }

  async function apagarBloco(bloco) {
    setBlocos((prev) => prev.filter((b) => b.id !== bloco.id));

    const resposta = await api.delete(`/blocos_mapa_loja/${bloco.id}`);
    if (!resposta.data.status) {
      enqueueSnackbar(resposta.data.message, { variant: 'error' });
      setBlocos((prev) => [...prev, bloco]);
    }
  }

  // Recarrega só um endereço específico (não o mapa inteiro) pra refletir na
  // hora qualquer mudança (categoria, produtos, quantidade de andares, etc)
  // sem pesar a tela com um reload geral.
  async function recarregarEndereco(id) {
    try {
      const { data } = await getListAllAPI(
        'enderecos_exposicao_mercadorias',
        ['id', 'asc'],
        { id: [id] },
        ['produto', 'andaresProdutos'],
      );
      if (data.length > 0) {
        setEnderecos((prev) => prev.map((e) => (e.id === id ? data[0] : e)));
      }
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    }
  }

  function handleEditarEndereco(id) {
    setDialogoEditarEndereco({ open: true, id });
  }

  async function handleFecharEditarEndereco() {
    const idEditado = dialogoEditarEndereco.id;
    setDialogoEditarEndereco({ open: false, id: null });
    if (idEditado) await recarregarEndereco(idEditado);
  }

  function handleFecharMenuEndereco() {
    setMenuEndereco({ endereco: null });
  }

  function handleEscolherEditar(id) {
    handleFecharMenuEndereco();
    handleEditarEndereco(id);
  }

  function handleEscolherVisualizar(id) {
    handleFecharMenuEndereco();
    setIdEnderecoVisualizado(id);
    if (refDialogoShow.current) refDialogoShow.current.handleOpen(id);
  }

  async function handleFecharVisualizarEndereco() {
    const id = idEnderecoVisualizado;
    setIdEnderecoVisualizado(null);
    if (id) await recarregarEndereco(id);
  }

  function handleCelulaClick(x, y, endereco, bloco) {
    if (categoriaSelecionada) {
      if (endereco) return;

      if (bloco) {
        apagarBloco(bloco);
      } else {
        criarBloco(x, y);
      }
      return;
    }

    if (endereco) setMenuEndereco({ endereco });
  }

  // O clique-e-arraste funciona nos dois sentidos: se a célula onde o
  // arraste começa está vazia, o arraste pinta (igual já funcionava); se
  // começa numa célula já pintada, o arraste passa a apagar os blocos que
  // for encontrando pelo caminho — assim dá pra despintar vários de uma vez,
  // do mesmo jeito que já dava pra pintar vários.
  function handleCelulaMouseDown(x, y, endereco, bloco) {
    if (!categoriaSelecionada || endereco) return;
    mouseDownRef.current = true;
    modoArrasteRef.current = bloco ? 'apagar' : 'pintar';
    if (bloco) {
      apagarBloco(bloco);
    } else {
      criarBloco(x, y);
    }
  }

  function handleCelulaMouseEnter(x, y, endereco, bloco) {
    if (!categoriaSelecionada || endereco || !mouseDownRef.current) return;

    if (modoArrasteRef.current === 'apagar') {
      if (bloco) apagarBloco(bloco);
    } else if (!bloco) {
      criarBloco(x, y);
    }
  }

  function handleCategoriaClick(categoria) {
    setCategoriaSelecionada((prev) => (prev === categoria ? null : categoria));
  }

  const naoPosicionados = enderecos.filter(
    (e) =>
      e.posicao_x === null ||
      e.posicao_x === undefined ||
      e.posicao_y === null ||
      e.posicao_y === undefined,
  );

  function possuiProdutoAtivo(endereco) {
    if (endereco.produto) return true;
    return (endereco.andaresProdutos || []).some(
      (ap) => ap.andar === 1 && ap.status === STATUS_ATIVO,
    );
  }

  const contagemPorCategoria = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    // Só entram na contagem os endereços já posicionados neste mapa — os
    // "sem posição" já têm o próprio contador na lista da esquerda.
    const doCategoriaNoMapa = enderecos.filter(
      (e) =>
        e.categoria === c.categoria &&
        e.posicao_x !== null &&
        e.posicao_x !== undefined &&
        e.posicao_y !== null &&
        e.posicao_y !== undefined,
    );
    const preenchidos = doCategoriaNoMapa.filter(possuiProdutoAtivo).length;
    contagemPorCategoria[c.categoria] = {
      preenchidos,
      disponiveis: doCategoriaNoMapa.length - preenchidos,
    };
  });

  const mapaEnderecosPorCelula = {};
  enderecos.forEach((e) => {
    if (e.posicao_x !== null && e.posicao_x !== undefined && e.posicao_y !== null && e.posicao_y !== undefined) {
      mapaEnderecosPorCelula[getChaveCelula(e.posicao_x, e.posicao_y)] = e;
    }
  });

  const mapaBlocosPorCelula = {};
  blocos.forEach((b) => {
    mapaBlocosPorCelula[getChaveCelula(b.posicao_x, b.posicao_y)] = b;
  });

  const corPorCategoria = {};
  CATEGORIAS_BLOCO_MAPA_LOJA.forEach((c) => {
    corPorCategoria[c.categoria] = c.cor;
  });

  const corPorCategoriaEndereco = {};
  const corBordaPorCategoriaEndereco = {};
  CATEGORIAS_ENDERECO_EXPOSICAO.forEach((c) => {
    corPorCategoriaEndereco[c.categoria] = c.cor;
    corBordaPorCategoriaEndereco[c.categoria] = c.corBorda;
  });

  const celulas = [];
  for (let y = 0; y < altura; y += 1) {
    for (let x = 0; x < largura; x += 1) {
      celulas.push({
        x,
        y,
        endereco: mapaEnderecosPorCelula[getChaveCelula(x, y)] || null,
        bloco: mapaBlocosPorCelula[getChaveCelula(x, y)] || null,
      });
    }
  }

  return (
    <>
    <Dialog
      onClose={handleClose}
      aria-labelledby="dialogo-mapa-loja"
      open={open}
      fullScreen
    >
      <DialogTitle onClose={handleClose}>{`Mapa da Loja - ${nomeMapa}`}</DialogTitle>
      <Divider />
      <MuiDialogContent className={classes.content}>
        {carregando ? (
          <Backdrop open className={classes.backdrop}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <>
            <div
              className={classes.sidebar}
              onDragOver={handleDragOver}
              onDrop={handleDropRemover}
            >
              <div className={classes.cabecalhoFixo}>
                <div className={classes.cabecalhoSidebar}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                    Endereços sem posição
                  </Typography>
                  <Chip size="small" label={naoPosicionados.length} />
                </div>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.dicaSidebar}
                >
                  Arraste um endereço posicionado no mapa pra cá pra removê-lo
                </Typography>
                <Divider className={classes.divisorSidebar} />
              </div>
              <div
                className={`${classes.listaScrollavel} ${classes.listaCardsEnderecos}`}
              >
                {naoPosicionados.map((e) => (
                  <div
                    key={e.id}
                    className={classes.cardEndereco}
                    style={{
                      backgroundColor: corPorCategoriaEndereco[e.categoria],
                      borderColor: corBordaPorCategoriaEndereco[e.categoria],
                    }}
                    draggable
                    onDragStart={(event) => handleDragStart(event, e.id)}
                  >
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {e.codigo}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
            <div className={classes.areaMapaWrapper} ref={areaMapaRef}>
              <div className={classes.areaMapa}>
                <div
                  className={classes.grid}
                  style={{
                    gridTemplateColumns: `repeat(${largura}, ${tamanhoCelula}px)`,
                    gridTemplateRows: `repeat(${altura}, ${tamanhoCelula}px)`,
                    gap: `${GAP_CELULA}px`,
                    cursor: categoriaSelecionada ? 'crosshair' : 'default',
                  }}
                >
                {celulas.map(({ x, y, endereco, bloco }) => (
                  <div
                    key={getChaveCelula(x, y)}
                    className={`${classes.celula} ${
                      endereco ? classes.celulaOcupada : ''
                    }`}
                    style={
                      endereco
                        ? {
                            backgroundColor: corPorCategoriaEndereco[endereco.categoria],
                            borderColor: corBordaPorCategoriaEndereco[endereco.categoria],
                          }
                        : BLOCOS_CATEGORIA_HABILITADO && bloco
                        ? { backgroundColor: corPorCategoria[bloco.categoria] }
                        : undefined
                    }
                    onDragOver={handleDragOver}
                    onDrop={(event) => handleDrop(event, x, y)}
                    onClick={() => handleCelulaClick(x, y, endereco, bloco)}
                    onMouseDown={() => handleCelulaMouseDown(x, y, endereco, bloco)}
                    onMouseEnter={() => handleCelulaMouseEnter(x, y, endereco, bloco)}
                    draggable={!!endereco}
                    onDragStart={
                      endereco
                        ? (event) => handleDragStart(event, endereco.id)
                        : undefined
                    }
                  >
                    {endereco ? (
                      <div className={classes.celulaConteudo}>
                        <Typography
                          style={{
                            fontWeight: 'bold',
                            fontSize: `${Math.max(tamanhoCelula * 0.3, 9)}px`,
                            lineHeight: 1.1,
                          }}
                        >
                          {endereco.codigo}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                ))}
                </div>
              </div>
              <Paper className={classes.controlesZoom} elevation={3}>
                <IconButton size="small" onClick={handleZoomOut}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" className={classes.labelZoom}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton size="small" onClick={handleZoomIn}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleZoomReset}
                  title="Ajustar à tela"
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Paper>
            </div>
            <div className={classes.sidebarDireita}>
              <div className={classes.cabecalhoFixo}>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                  Categoria do endereço
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.dicaSidebar}
                >
                  Legenda das cores do mapa
                </Typography>
                <Divider className={classes.divisorSidebar} />
                <div className={classes.legendaCategoriaEndereco}>
                  {CATEGORIAS_ENDERECO_EXPOSICAO.map((c) => (
                    <div key={c.categoria}>
                      <div className={classes.legendaCategoriaEnderecoItem}>
                        <div
                          className={classes.legendaCategoriaEnderecoCor}
                          style={{ backgroundColor: c.cor }}
                        />
                        <Typography variant="caption">{c.label}</Typography>
                      </div>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        className={classes.legendaCategoriaEnderecoContagem}
                      >
                        {`${contagemPorCategoria[c.categoria].preenchidos} preenchido${
                          contagemPorCategoria[c.categoria].preenchidos === 1 ? '' : 's'
                        } · ${contagemPorCategoria[c.categoria].disponiveis} disponível${
                          contagemPorCategoria[c.categoria].disponiveis === 1 ? '' : 'is'
                        }`}
                      </Typography>
                    </div>
                  ))}
                </div>
                {BLOCOS_CATEGORIA_HABILITADO ? (
                  <Divider className={classes.divisorSidebar} />
                ) : null}
              </div>
              {BLOCOS_CATEGORIA_HABILITADO ? (
                <div className={classes.listaScrollavel}>
                  {CATEGORIAS_BLOCO_MAPA_LOJA.map((c) => (
                    <div
                      key={c.categoria}
                      className={`${classes.categoriaChip} ${
                        categoriaSelecionada === c.categoria
                          ? classes.categoriaChipSelecionada
                          : ''
                      }`}
                      onClick={() => handleCategoriaClick(c.categoria)}
                    >
                      <div
                        className={classes.categoriaCor}
                        style={{ backgroundColor: c.cor }}
                      />
                      <Typography variant="body2">{c.label}</Typography>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </>
        )}
      </MuiDialogContent>
    </Dialog>
    <DialogoEditarEndereco
      open={dialogoEditarEndereco.open}
      id={dialogoEditarEndereco.id}
      onClose={handleFecharEditarEndereco}
      isDialogoConfirmacaoActions
    />
    {menuEndereco.endereco
      ? createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20000,
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseDown={handleFecharMenuEndereco}
          >
            <Paper
              elevation={8}
              style={{ minWidth: '260px', borderRadius: '12px', overflow: 'hidden' }}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '14px 16px',
                }}
              >
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor:
                      corPorCategoriaEndereco[menuEndereco.endereco.categoria],
                  }}
                />
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                  {`Endereço ${menuEndereco.endereco.codigo}`}
                </Typography>
              </div>
              <Divider />
              <div style={{ padding: '8px' }}>
                <MenuItem
                  onClick={() => handleEscolherVisualizar(menuEndereco.endereco.id)}
                  style={{ borderRadius: '8px', padding: '10px 12px' }}
                >
                  <ListItemIcon style={{ minWidth: '36px' }}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Visualizar" />
                </MenuItem>
                <MenuItem
                  onClick={() => handleEscolherEditar(menuEndereco.endereco.id)}
                  style={{ borderRadius: '8px', padding: '10px 12px' }}
                >
                  <ListItemIcon style={{ minWidth: '36px' }}>
                    <EditOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Editar" />
                </MenuItem>
              </div>
            </Paper>
          </div>,
          document.body,
        )
      : null}
    <DialogoShow ref={refDialogoShow} handleClose={handleFecharVisualizarEndereco} />
    </>
  );
});

export default DialogoMapaLoja;
